import { useRef, useState } from 'react'
import ReactDropzone from 'react-dropzone'
import AvatarEditor from 'react-avatar-editor'
import Slider from 'rc-slider'
import 'rc-slider/assets/index.css'

import ToastService from 'services/toast.service'
import UserService from 'services/user.service'

import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import PhatButton from 'components/formElements/PhatButton'

import styles from './imageManipulationOverlay.module.scss'
import Icon from 'components/elements/Icon'
import LineLoader from 'components/elements/LineLoader'

type Props = {
  open: boolean
  onClose: () => void
  onImageUploaded?: (imageUrl: string) => void
}

const UPLOADED_CARD_WIDTH = 672
const UPLOADED_CARD_HEIGHT = 936

const ImageManipulationOverlay = ({ open, onClose, onImageUploaded }: Props) => {
  const editorRef = useRef<AvatarEditor>(null)

  const [file, setFile] = useState<File | null>(null)
  const [scale, setScale] = useState(100)
  const [uploadingImage, setUploadingImage] = useState(false)

  const handleDownloadPreview = () => {
    if (!editorRef.current) return

    const canvas = editorRef.current.getImage()
    const url = canvas.toDataURL('image/png')
    const link = document.createElement('a')

    link.download = `editedCardCrop-${new Date().toISOString()}.png`
    link.href = url
    link.click()
  }

  const handleUploadImage = () => {
    if (!editorRef.current) return

    setUploadingImage(true)

    // This canvase comes in as the size of the image as it was originally opened (so fuckin massive in some cases)
    const image = editorRef.current.getImage()

    // Opening a new canvase so we can resize the cropped image so it'll actually upload
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    canvas.width = UPLOADED_CARD_WIDTH
    canvas.height = UPLOADED_CARD_HEIGHT

    // Draw the original image onto the canvas, scaling it to the new dimensions
    ctx?.drawImage(image, 0, 0, UPLOADED_CARD_WIDTH, UPLOADED_CARD_HEIGHT)

    const resizedImageData = canvas.toDataURL()

    fetch(resizedImageData)
      .then(res => res.blob())
      .then(blob => UserService.uploadImage(new File([blob], 'tmp.png')))
      .then(({ imageUrl }) => {
        if (onImageUploaded) onImageUploaded(imageUrl)

        setFile(null)
        onClose()
      })
      .catch(err => ToastService.create(err, 'Image upload', 'error'))
      .finally(() => setUploadingImage(false))
  }

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <h3 className={styles.header}>Custom Card image uploader</h3>

      <div className={styles.content}>
        <div className={styles.left}>
          {!file && (
            <ReactDropzone
              // 1.5MB
              maxSize={1500000}
              className={styles.fileSelector}
              onDrop={(files: File[]) => setFile(files[0])}
              onDropRejected={(file: File[]) => {
                if (file[0]?.size > 1500000)
                  return ToastService.create('File size too large. Please use a smaller image file.', 'Image upload')

                ToastService.create(
                  'Unable to open selected image. If this problem persists please contact support',
                  'Image upload',
                )
              }}
              accept="image/jpeg, image/png, image/webp">
              <div>
                <Icon name="upload" /> Select file to upload{' '}
              </div>

              <div>(Max 1.5MB)</div>
            </ReactDropzone>
          )}

          {file && (
            <AvatarEditor
              ref={editorRef}
              style={{ filter: uploadingImage ? 'brightness(.6)' : undefined }}
              className={styles.imageEditor}
              image={URL.createObjectURL(file)}
              width={225}
              height={312}
              border={0}
              scale={scale / 100}
              rotate={0}
            />
          )}
        </div>

        <div className={styles.right}>
          <h3>Card editor controls</h3>

          <div className={styles.warning}>
            <p>Only upload images you own or have permission to use. Do not upload images featuring mature content. </p>

            <p>
              Failure to abide by these rules may result in restricted access to this feature or banning from the site
              entirely. We reserve the right to remove any and all images from your account for any reason and without
              warning.
            </p>
          </div>

          <p>
            Select the image you want to upload, then use your mouse and the controls to position and scale the image to
            fit into the card frame. The card will be cropped, then uploaded.
          </p>

          <div>
            <div>Image scale ({scale}%)</div>
            <Slider value={scale} min={1} max={300} onChange={value => setScale(Number(value))} />
          </div>
          <div className={styles.actions}>
            <PhatButton disabled={uploadingImage || !file} icon="close" color="red" onClick={() => setFile(null)}>
              Clear opened image
            </PhatButton>

            <PhatButton disabled={uploadingImage || !file} icon="download" color="blue" onClick={handleDownloadPreview}>
              Download cropped image
            </PhatButton>

            <PhatButton disabled={uploadingImage || !file} icon="upload" color="green" onClick={handleUploadImage}>
              Upload cropped image
            </PhatButton>
          </div>

          <div>{uploadingImage && <LineLoader />}</div>
        </div>
      </div>
    </GlobalOverlayStack>
  )
}

export default ImageManipulationOverlay
